import { create } from "zustand";

export type ToastKind = "success" | "error" | "info" | "warning";

export interface Toast {
	id: number;
	kind: ToastKind;
	title: string;
	message?: string;
	/** Auto-dismiss after this many ms. 0 keeps it until manually dismissed. */
	durationMs: number;
	/**
	 * Sprint α-bis-2 (2026-05-09): progress-mode toast.
	 *
	 * When ``progressKey`` is set the toast is "sticky" — it doesn't
	 * auto-dismiss until ``markProgressDone`` is called. ToastViewport
	 * renders it with a spinner, the current phase, elapsed time, and
	 * (when known) a determinate progress bar. Lets the user SEE that
	 * heavy autonomous work is running (daily cycle / EOD / memory
	 * consolidation / evolution mining) instead of wondering why the
	 * app feels slow.
	 *
	 * Keyed dedup: subsequent ``upsertProgress(key, ...)`` updates an
	 * existing toast in place rather than spawning a new one. Same key
	 * across started → progress-update → completed events keeps it
	 * a single line.
	 */
	progressKey?: string;
	/** ISO timestamp when the progress toast was first opened. */
	startedAt?: string;
	/** Optional 0..1 progress fraction. None = indeterminate (spinner only). */
	progress?: number;
	/** Optional current-phase label (e.g. "Generating EOD report..."). */
	phase?: string;
	/** Optional terminal state — drives the auto-dismiss after a brief hold. */
	progressState?: "running" | "completed" | "failed" | "skipped";
}

interface ToastState {
	toasts: Toast[];
	push: (t: Omit<Toast, "id" | "durationMs"> & { durationMs?: number }) => number;
	/**
	 * Open a sticky progress-mode toast OR update an existing one if a
	 * toast with the same ``progressKey`` is already open. Returns the
	 * toast id (new or existing).
	 */
	upsertProgress: (
		t: Omit<Toast, "id" | "durationMs" | "progressState"> & {
			progressKey: string;
			startedAt?: string;
			progressState?: Toast["progressState"];
		},
	) => number;
	/**
	 * Mark a progress toast as terminal. Updates kind + state, leaves
	 * the toast on screen for ``holdMs`` (default 2.5s) so the user
	 * can see "✓ Done" / "⚠ Failed", then auto-dismisses.
	 */
	markProgressDone: (
		key: string,
		state: "completed" | "failed" | "skipped",
		opts?: { title?: string; message?: string; holdMs?: number },
	) => void;
	dismiss: (id: number) => void;
	dismissByKey: (key: string) => void;
	clear: () => void;
}

let nextId = 1;

/**
 * Lightweight global toast store. Keep messages short and actionable —
 * the user already knows what they clicked; the toast explains what went wrong.
 */
export const useToast = create<ToastState>((set, get) => ({
	toasts: [],
	push: ({ kind, title, message, durationMs }) => {
		const id = nextId++;
		const effectiveDuration = durationMs ?? (kind === "error" ? 10000 : 7000);
		set((s) => ({ toasts: [...s.toasts, { id, kind, title, message, durationMs: effectiveDuration }] }));
		if (effectiveDuration > 0) {
			window.setTimeout(() => get().dismiss(id), effectiveDuration);
		}
		return id;
	},
	upsertProgress: ({
		kind,
		title,
		message,
		progressKey,
		startedAt,
		progress,
		phase,
		progressState,
	}) => {
		const existing = get().toasts.find((t) => t.progressKey === progressKey);
		if (existing) {
			set((s) => ({
				toasts: s.toasts.map((t) =>
					t.progressKey === progressKey
						? {
								...t,
								// Progress-mode updates can change everything except
								// the id, durationMs, and the original startedAt.
								kind: kind ?? t.kind,
								title: title ?? t.title,
								message: message ?? t.message,
								progress: progress ?? t.progress,
								phase: phase ?? t.phase,
								progressState: progressState ?? t.progressState,
							}
						: t,
				),
			}));
			return existing.id;
		}
		const id = nextId++;
		set((s) => ({
			toasts: [
				...s.toasts,
				{
					id,
					kind,
					title,
					message,
					// Sticky by default — terminal handler dismisses after a hold.
					durationMs: 0,
					progressKey,
					startedAt: startedAt ?? new Date().toISOString(),
					progress,
					phase,
					progressState: progressState ?? "running",
				},
			],
		}));
		return id;
	},
	markProgressDone: (key, state, opts) => {
		const holdMs = opts?.holdMs ?? 2500;
		const found = get().toasts.find((t) => t.progressKey === key);
		if (!found) return;
		const terminalKind: ToastKind =
			state === "completed" ? "success" : state === "failed" ? "error" : "warning";
		set((s) => ({
			toasts: s.toasts.map((t) =>
				t.progressKey === key
					? {
							...t,
							kind: terminalKind,
							title: opts?.title ?? t.title,
							message: opts?.message ?? t.message,
							progressState: state,
							progress: state === "completed" ? 1.0 : t.progress,
						}
					: t,
			),
		}));
		// Auto-dismiss after the hold.
		window.setTimeout(() => get().dismissByKey(key), holdMs);
	},
	dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
	dismissByKey: (key) =>
		set((s) => ({ toasts: s.toasts.filter((t) => t.progressKey !== key) })),
	clear: () => set({ toasts: [] }),
}));

/** Imperative helpers for non-component code (api helpers, stores, etc.). */
export const toast = {
	success: (title: string, message?: string) =>
		useToast.getState().push({ kind: "success", title, message }),
	error: (title: string, message?: string) =>
		useToast.getState().push({ kind: "error", title, message }),
	info: (title: string, message?: string) =>
		useToast.getState().push({ kind: "info", title, message }),
	warning: (title: string, message?: string) =>
		useToast.getState().push({ kind: "warning", title, message }),
	/**
	 * Open or update a sticky progress toast. Use the same ``progressKey``
	 * across started → progress-update → completed events to keep one
	 * line on screen.
	 */
	progress: (
		progressKey: string,
		title: string,
		opts?: {
			message?: string;
			phase?: string;
			progress?: number;
			kind?: ToastKind;
		},
	) =>
		useToast.getState().upsertProgress({
			kind: opts?.kind ?? "info",
			title,
			message: opts?.message,
			progressKey,
			phase: opts?.phase,
			progress: opts?.progress,
		}),
	progressDone: (
		progressKey: string,
		state: "completed" | "failed" | "skipped" = "completed",
		opts?: { title?: string; message?: string },
	) => useToast.getState().markProgressDone(progressKey, state, opts),
};
